import { buildRuntimeCacheKey } from '@emporion/plugin-sdk/block-contract'
import type {
  FieldRendererRuntimeErrorCode,
  FieldRendererRuntimeResponse,
  FieldRendererRuntimeSuccessResponse,
} from '@emporion/plugin-sdk/renderer-runtime'
import { API_BASE } from '../../../../shared/api/client'
import { FetchPolicyError, fetchWithPolicy } from '../../../../shared/api/fetchPolicy'
import type { CatalogProductFieldDefinition } from '../../../pluginFields'
import type { CatalogProduct } from '../../../types'
import { getProductFieldRendererKey, resolveRendererForField } from './registry'

const SUPPORTED_RUNTIME_FIELD_TYPES = new Set<CatalogProductFieldDefinition['type']>([
  'text',
  'textarea',
  'number',
  'boolean',
  'select',
  'richtext',
  'media',
  'date',
])

const MAX_RENDERER_KEY_LENGTH = 120
const VALID_RENDERER_KEY_PATTERN = /^[a-zA-Z0-9._-]+$/

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length ? value.trim() : undefined

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null)

const isRuntimeInputValue = (value: unknown): boolean => {
  if (value === null) return true
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return true
  if (!Array.isArray(value)) return false
  return value.every((entry) => isRuntimeInputValue(entry))
}

const isValidRuntimeRendererKey = (value: string): boolean =>
  value.length > 0 && value.length <= MAX_RENDERER_KEY_LENGTH && VALID_RENDERER_KEY_PATTERN.test(value)

export type ProductFieldRuntimeField = {
  id: string
  key: string
  label: string
  type: CatalogProductFieldDefinition['type']
  rendererKey: string
  storefront: {
    zone: CatalogProductFieldDefinition['storefront']['zone']
    order: number
  }
}

export type ProductFieldRuntimeProduct = {
  id: string
  slug: string
  productType?: string
}

export type ProductFieldRuntimeContext = {
  locale?: string
}

export type ProductFieldRuntimeRequest = {
  requestKey: string
  rendererKey: string
  field: ProductFieldRuntimeField
  product: ProductFieldRuntimeProduct
  value: unknown
  context: ProductFieldRuntimeContext
}

export type ProductFieldRuntimeFailureResponse = {
  ok: false
  code: FieldRendererRuntimeErrorCode | 'NETWORK_ERROR'
  message: string
}

export type ProductFieldRuntimeResponse = FieldRendererRuntimeResponse | ProductFieldRuntimeFailureResponse

const isSuccessResponse = (value: unknown): value is FieldRendererRuntimeSuccessResponse => {
  if (!isPlainObject(value) || value.ok !== true) return false
  return (
    typeof value.rendererKey === 'string' &&
    typeof value.html === 'string' &&
    typeof value.hash === 'string' &&
    isValidRuntimeRendererKey(value.rendererKey.trim()) &&
    value.rendererKey.trim().length > 0 &&
    value.hash.trim().length > 0 &&
    (value.version === undefined || typeof value.version === 'string')
  )
}

const isFailureResponse = (value: unknown): value is ProductFieldRuntimeFailureResponse => {
  if (!isPlainObject(value) || value.ok !== false) return false
  return typeof value.code === 'string' && typeof value.message === 'string'
}

const toRuntimeField = (field: CatalogProductFieldDefinition, rendererKey: string): ProductFieldRuntimeField => ({
  id: field.id,
  key: field.key,
  label: field.label,
  type: field.type,
  rendererKey,
  storefront: {
    zone: field.storefront.zone,
    order: field.storefront.order,
  },
})

const toRuntimeProduct = (productData: CatalogProduct): ProductFieldRuntimeProduct | null => {
  const id = asString(productData._id)
  const slug = asString(productData.slug)
  if (!id || !slug) return null

  const productType = asString(productData.productType)
  return {
    id,
    slug,
    ...(productType ? { productType } : {}),
  }
}

export const buildProductFieldRuntimeRequest = ({
  field,
  value,
  productData,
  locale,
}: {
  field: CatalogProductFieldDefinition
  value: unknown
  productData: CatalogProduct | null
  locale?: string
}): ProductFieldRuntimeRequest | null => {
  const rendererKey = getProductFieldRendererKey(field)
  if (!rendererKey) return null
  if (rendererKey === 'text') return null
  if (!isValidRuntimeRendererKey(rendererKey)) return null
  if (resolveRendererForField(field)) return null
  if (!SUPPORTED_RUNTIME_FIELD_TYPES.has(field.type)) return null
  if (!isRuntimeInputValue(value)) return null

  const product = productData ? toRuntimeProduct(productData) : null
  if (!product) return null

  const requestKey = buildRuntimeCacheKey(
    'field-renderer',
    rendererKey,
    field.key,
    field.type,
    field.storefront.zone,
    field.storefront.order,
    value,
    product.id,
    product.slug,
    product.productType ?? '',
    locale ?? '',
  )

  return {
    requestKey,
    rendererKey,
    field: toRuntimeField(field, rendererKey),
    product,
    value,
    context: locale ? { locale } : {},
  }
}

const parseErrorResponse = async (response: Response): Promise<ProductFieldRuntimeFailureResponse> => {
  try {
    const contentType = response.headers.get('content-type') || ''
    if (contentType.includes('application/json')) {
      const payload = (await response.json()) as unknown
      if (isFailureResponse(payload)) return payload
      if (isPlainObject(payload) && typeof payload.error === 'string' && payload.error.trim()) {
        return {
          ok: false,
          code: 'RENDER_RUNTIME_FAILURE',
          message: payload.error.trim(),
        }
      }
      if (isPlainObject(payload) && typeof payload.message === 'string' && payload.message.trim()) {
        return {
          ok: false,
          code: 'RENDER_RUNTIME_FAILURE',
          message: payload.message.trim(),
        }
      }
    } else {
      const text = (await response.text()).trim()
      if (text) {
        return {
          ok: false,
          code: 'RENDER_RUNTIME_FAILURE',
          message: text.slice(0, 300),
        }
      }
    }
  } catch {
    // fall through to the generic error below
  }

  return {
    ok: false,
    code: 'RENDER_RUNTIME_FAILURE',
    message: `HTTP ${response.status}: ${response.statusText}`,
  }
}

const mapFetchError = (error: unknown): ProductFieldRuntimeFailureResponse => {
  if (error instanceof FetchPolicyError) {
    if (error.kind === 'timeout') {
      return {
        ok: false,
        code: 'RENDER_TIMEOUT',
        message: 'Renderer runtime request timed out',
      }
    }
    if (error.kind === 'aborted') {
      return {
        ok: false,
        code: 'RENDER_RUNTIME_FAILURE',
        message: 'Renderer runtime request aborted',
      }
    }
  }

  return {
    ok: false,
    code: 'NETWORK_ERROR',
    message: error instanceof Error ? error.message : 'Renderer runtime request failed',
  }
}

export const fetchProductFieldRuntime = async (
  request: ProductFieldRuntimeRequest,
  signal?: AbortSignal,
): Promise<ProductFieldRuntimeResponse> => {
  try {
    const response = await fetchWithPolicy({
      request: `${API_BASE}/plugin-runner/render/field-renderer`,
      init: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rendererKey: request.rendererKey,
          field: request.field,
          product: request.product,
          value: request.value,
          context: request.context,
        }),
        ...(signal ? { signal } : {}),
      },
      timeoutMs: 6_000,
      retries: 0,
    })

    if (!response.ok) {
      return parseErrorResponse(response)
    }

    const payload = (await response.json()) as unknown
    if (isSuccessResponse(payload)) {
      if (payload.rendererKey.trim() !== request.rendererKey.trim()) {
        return {
          ok: false,
          code: 'INVALID_RUNTIME_RESPONSE',
          message: 'Renderer runtime response rendererKey mismatch',
        }
      }
      return payload
    }

    if (isFailureResponse(payload)) return payload

    return {
      ok: false,
      code: 'INVALID_RUNTIME_RESPONSE',
      message: 'Invalid field renderer runtime response',
    }
  } catch (error) {
    return mapFetchError(error)
  }
}
